import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, PAT_RESULTS } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Zap, Printer, Plus, FileDown } from "lucide-react";
import { toast } from "sonner";

const ORDER = { "PAT overdue": 0, "PAT due soon": 1, Untested: 2, Tested: 3, "PAT valid": 4 };
const FILTERS = [
  { id: "all", label: "All" },
  { id: "PAT overdue", label: "Overdue" },
  { id: "PAT due soon", label: "Due soon" },
  { id: "Untested", label: "Untested" },
  { id: "PAT valid", label: "Valid" },
];

export default function PatTests() {
  const nav = useNavigate();
  const [assets, setAssets] = useState(null);
  const [filter, setFilter] = useState("all");
  const [rec, setRec] = useState(null);

  const load = useCallback(() => api.listAssets().then(setAssets).catch(() => setAssets([])), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rec.date) { toast.error("Test date required"); return; }
    try { await api.addPat(rec.assetId, rec); toast.success("PAT recorded"); const aid = rec.assetId; setRec(null); await load(); nav(`/assets/${aid}/pat-tag`); }
    catch { toast.error("Failed"); }
  };

  if (assets === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const counts = assets.reduce((m, a) => { m[a.pat_status] = (m[a.pat_status] || 0) + 1; return m; }, {});
  const rows = [...assets].sort((a, b) => (ORDER[a.pat_status] ?? 9) - (ORDER[b.pat_status] ?? 9))
    .filter((a) => filter === "all" ? true : a.pat_status === filter);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Electrical safety</span>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Tag Tests (PAT)</h1>
          </div>
        </div>
        <Button data-testid="pat-pdf-btn" onClick={() => nav("/reports/pat")} variant="outline" className="gap-2 tap-scale"><FileDown className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span></Button>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {["PAT overdue", "PAT due soon", "Untested", "PAT valid"].map((k) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <div className="font-mono text-2xl font-extrabold text-foreground">{counts[k] || 0}</div>
            <div className="mt-1"><StatusBadge value={k} /></div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`pat-filter-${f.id}`} onClick={() => setFilter(f.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold tap-scale ${filter === f.id ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{f.label}</button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">No assets in this category.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} data-testid={`pat-row-${a.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5">
              <button onClick={() => nav(`/assets/${a.id}`)} className="min-w-0 flex-1 text-left tap-scale">
                <div className="truncate text-sm font-semibold text-foreground">{a.asset_code} · {a.name}</div>
                <div className="truncate text-xs text-muted-foreground">{a.location || "—"}{a.pat_next_due ? ` · next due ${a.pat_next_due}` : ""}</div>
              </button>
              <StatusBadge value={a.pat_status} />
              <Button data-testid={`pat-record-${a.id}`} size="sm" variant="outline" onClick={() => setRec({ assetId: a.id, date: new Date().toISOString().slice(0, 10), result: "Pass", tested_by: "", next_due: "", notes: "" })} className="gap-1.5 tap-scale"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Record</span></Button>
              <Button size="sm" variant="ghost" disabled={!a.pat_tests?.length} onClick={() => nav(`/assets/${a.id}/pat-tag`)} className="h-9 w-9 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30"><Printer className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rec} onOpenChange={(o) => !o && setRec(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record PAT test</DialogTitle><DialogDescription>Save a result, then a fresh tag opens to print.</DialogDescription></DialogHeader>
          {rec && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Date tested</Label><Input data-testid="pat-date" type="date" value={rec.date} onChange={(e) => setRec({ ...rec, date: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Result</Label><Select value={rec.result} onValueChange={(v) => setRec({ ...rec, result: v })}><SelectTrigger data-testid="pat-result" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{PAT_RESULTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Tested by</Label><Input value={rec.tested_by} onChange={(e) => setRec({ ...rec, tested_by: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Next due</Label><Input data-testid="pat-next-due" type="date" value={rec.next_due} onChange={(e) => setRec({ ...rec, next_due: e.target.value })} className="bg-secondary/40" /></div>
              </div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label><Textarea value={rec.notes} onChange={(e) => setRec({ ...rec, notes: e.target.value })} className="bg-secondary/40" /></div>
              <Button data-testid="pat-submit" onClick={submit} className="w-full bg-primary text-primary-foreground tap-scale hover:bg-primary/90">Save & print tag</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
